import { HTMLAttributes, ReactNode } from 'react'

import { cn } from '@/lib/utils'

export type MarqueeProps = HTMLAttributes<HTMLDivElement> & {
  /** Content to be displayed in the marquee. */
  children: ReactNode
  /** Direction of marquee movement. Default is 'vertical'. */
  direction?: 'vertical' | 'horizontal'
  /** Whether to pause the animation on hover. Default is false. */
  pauseOnHover?: boolean
  /** Whether to reverse the animation direction. Default is false. */
  reverse?: boolean
  /** Whether to apply a fade effect. Default is false. */
  fade?: boolean
  /** Additional className for the inner container. */
  innerClassName?: string
  /** Number of times to copy the children for smooth looping. Default is 2. */
  numberOfCopies?: number
  /** Whether the marquee animation runs automatically. Default is true. */
  autoPlay?: boolean
  /** Duration of one complete animation cycle in seconds. */
  durationSeconds?: number
  /** Gap between marquee items in pixels. */
  gapPx?: number
}

export default function Marquee({
  children,
  direction = 'vertical',
  pauseOnHover = false,
  reverse = false,
  fade = false,
  className,
  innerClassName,
  numberOfCopies = 2,
  autoPlay = true,
  durationSeconds = 25,
  gapPx = 16,
  style,
  ...rest
}: MarqueeProps) {
  const safeCopies = autoPlay ? Math.min(Math.max(Math.round(numberOfCopies), 1), 10) : 1
  const safeDuration = Math.min(Math.max(durationSeconds, 5), 120)
  const safeGap = Math.min(Math.max(gapPx, 0), 120)

  return (
    <div
      className={cn(
        'group flex gap-[1rem] overflow-hidden',
        direction === 'vertical' ? 'flex-row' : 'flex-col',
        className
      )}
      style={{
        ...style,
        gap: `${safeGap}px`,
        maskImage: fade
          ? `linear-gradient(${
              direction === 'vertical' ? 'to right' : 'to bottom'
            }, transparent 0%, rgba(0, 0, 0, 1.0) 10%, rgba(0, 0, 0, 1.0) 90%, transparent 100%)`
          : undefined,
        WebkitMaskImage: fade
          ? `linear-gradient(${
              direction === 'vertical' ? 'to right' : 'to bottom'
            }, transparent 0%, rgba(0, 0, 0, 1.0) 10%, rgba(0, 0, 0, 1.0) 90%, transparent 100%)`
          : undefined,
      }}
      {...rest}
    >
      {Array(safeCopies)
        .fill(0)
        .map((_, i) => (
          <div
            key={i}
            className={cn(
              'flex justify-around gap-[1rem] [--gap:1rem] shrink-0',
              direction === 'vertical' ? 'flex-row' : 'flex-col',
              autoPlay && (direction === 'vertical' ? 'animate-marquee-left' : 'animate-marquee-up'),
              pauseOnHover && 'group-hover:[animation-play-state:paused]',
              innerClassName
            )}
            style={{
              animationDuration: autoPlay ? `${safeDuration}s` : undefined,
              animationDirection: autoPlay && reverse ? 'reverse' : undefined,
              gap: `${safeGap}px`,
            }}
          >
            {children}
          </div>
        ))}
    </div>
  )
}
